/**
 * Contas do uso de cashback num pedido (spec `docs/specs/cashback-uso-no-cardapio.md` §6.1).
 * Só funções puras. O par no servidor é `backend/src/lib/cashback-pedido.ts`: o backend refaz
 * estas contas e recusa o pedido que não fechar, então as duas pontas têm de dar o mesmo número.
 *
 * Tudo é somado em centavos inteiros: em float, 10.99 + 9 dá 19.990000000000002.
 */

// Têm de ser iguais às do backend (`backend/src/lib/cashback-pedido.ts`); o valor online ainda não foi confirmado no sandbox do gateway.
export const MINIMO_A_PAGAR_NA_ENTREGA = 0.01
export const MINIMO_A_PAGAR_ONLINE = 5

/** Quanto precisa sobrar para pagar, conforme o `tipo` da forma de pagamento escolhida. */
export function minimoAPagar(tipoDaFormaDePagamento: string | null | undefined): number {
  if (tipoDaFormaDePagamento === 'ONLINE') return MINIMO_A_PAGAR_ONLINE
  return MINIMO_A_PAGAR_NA_ENTREGA
}

/** Folga que separa o ruído do float (19.99 * 100 dá 1998.9999999999998) de uma fração de centavo de verdade. */
const RUIDO_DO_FLOAT = 0.000001

/** Reais em centavos inteiros, no centavo mais próximo. É a conversão que o backend faz (`emCentavos`). */
export function emCentavos(reais: number): number {
  return Math.round(reais * 100)
}

/** Tira a fração de centavo, sempre para baixo: 1 centavo a mais de cashback é pedido recusado. */
function semFracaoDeCentavo(centavos: number): number {
  return Math.floor(centavos + RUIDO_DO_FLOAT)
}

export interface PedidoParaCalcularCashback {
  /** Saldo do cliente nesta loja, em reais (`saldoDisponivel` de `GET /me/cashback`). */
  saldoDisponivel: number
  /** Quanto dos itens a loja aceita em cashback, em % (`percentualMaxUso`). Zero quer dizer sem teto. */
  percentualMaxUso: number
  /** Soma do `valorTotal` dos itens do pedido, em reais. */
  totalDosItens: number
  /** Desconto do cupom, em reais. Zero quando o pedido não tem cupom. */
  descontoDoCupom: number
  /** Taxa de entrega mais taxa de conveniência, em reais. O cashback não abate as taxas, mas elas entram no total a pagar. */
  taxas: number
  /** `tipo` da forma de pagamento escolhida. Sem forma escolhida vale como pagamento na entrega. */
  tipoDaFormaDePagamento: string | null | undefined
}

/**
 * `maxUsavel` do §6.1: o máximo de cashback que dá para usar neste pedido, em reais com 2 casas.
 * É o menor de quatro limites, e nunca é negativo.
 */
export function maximoDeCashbackNoPedido(pedido: PedidoParaCalcularCashback): number {
  const itensEmCentavos = emCentavos(pedido.totalDosItens)
  const cupomEmCentavos = emCentavos(pedido.descontoDoCupom)
  const taxasEmCentavos = emCentavos(pedido.taxas)

  // Limite 1: o que o cliente tem.
  const saldoEmCentavos = semFracaoDeCentavo(pedido.saldoDisponivel * 100)

  // Limite 2: os itens depois do cupom. O cashback abate só itens, nunca as taxas.
  const baseEmCentavos = Math.max(0, itensEmCentavos - cupomEmCentavos)

  // Limite 3: o percentual que a loja aceita, sobre a base.
  const tetoEmCentavos = tetoDaLojaEmCentavos(baseEmCentavos, pedido.percentualMaxUso)

  // Limite 4: tem de sobrar o mínimo da forma de pagamento para pagar.
  const totalSemCashbackEmCentavos = itensEmCentavos + taxasEmCentavos - cupomEmCentavos
  const minimoEmCentavos = emCentavos(minimoAPagar(pedido.tipoDaFormaDePagamento))
  const sobraParaPagarEmCentavos = totalSemCashbackEmCentavos - minimoEmCentavos

  const maximoEmCentavos = Math.min(saldoEmCentavos, baseEmCentavos, tetoEmCentavos, sobraParaPagarEmCentavos)

  // Número inválido em qualquer entrada (resposta sem o campo, por exemplo) não vira cashback.
  if (!Number.isFinite(maximoEmCentavos)) return 0
  if (maximoEmCentavos <= 0) return 0
  return maximoEmCentavos / 100
}

/** Percentual zero quer dizer que a loja não pôs teto: vale a base inteira. É a regra do cashback e do PDV. */
function tetoDaLojaEmCentavos(baseEmCentavos: number, percentualMaxUso: number): number {
  if (percentualMaxUso <= 0) return baseEmCentavos
  return semFracaoDeCentavo((baseEmCentavos * percentualMaxUso) / 100)
}

export interface ValoresParaFecharOPedido {
  /** Soma dos itens, em reais: o `subtotal` do corpo do pedido. */
  subtotal: number
  /** Taxa de entrega mais taxa de conveniência, em reais: o `taxaEntrega` do corpo do pedido. */
  taxas: number
  /** Desconto do cupom, em reais. Zero quando o pedido não tem cupom. */
  descontoDoCupom: number
  /** Cashback que o cliente vai usar, em reais. Zero quando não usa. */
  valorCashback: number
}

export interface DescontoETotalDoPedido {
  /** Cupom + cashback: o `desconto` do corpo do pedido. */
  desconto: number
  /** Subtotal + taxas − desconto: o `total` do corpo do pedido. */
  total: number
}

/**
 * `desconto` e `total` do corpo do pedido, em reais com 2 casas. O backend refaz esta conta
 * (`conferirValoresDoPedidoComCashback`) e recusa o pedido em que ela não fecha.
 */
export function descontoETotalDoPedido(valores: ValoresParaFecharOPedido): DescontoETotalDoPedido {
  const descontoEmCentavos = emCentavos(valores.descontoDoCupom) + emCentavos(valores.valorCashback)
  const totalEmCentavos = emCentavos(valores.subtotal) + emCentavos(valores.taxas) - descontoEmCentavos

  return {
    desconto: descontoEmCentavos / 100,
    total: totalEmCentavos / 100,
  }
}
